import React, { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { InputAdornment, TextField } from '@mui/material'
import _, { isNil, isNumber, isString } from 'lodash'
import { Text, TextVariant } from '../Text'
import { InputWrapper } from './InputAssets'
import { InputProps } from './types'

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`

const LengthText = styled(Text)`
  position: absolute;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray7};
  bottom: 2px;
  right: 15px;
`

export const StyledTextField = styled(TextField).withConfig({
  // Typed as string: shouldForwardProp receives prop names, but TS infers the
  // base component's key union, so comparing to a custom prop has no overlap.
  shouldForwardProp: (prop: string | number) => prop !== 'customHeight',
})<{
  success?: boolean
  padding?: string
  disabled?: boolean
  customHeight?: string
}>`
  &.MuiTextField-root {
    width: 100%;

    .MuiInputBase-input {
      color: ${({ theme }) => theme.colors.white};

      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus,
      &:-webkit-autofill:active {
        -webkit-text-fill-color: ${({ theme }) => theme.colors.white} !important;
        transition: background-color 600s ease-in-out 0s;
      }

      ::placeholder {
        opacity: 1;
        color: ${({ theme, disabled }) => (disabled ? theme.colors.gray7 : theme.colors.gray3)};
        font-size: 12px;
        font-weight: 500;
      }
    }
    .MuiFilledInput-root {
      font-weight: 600;
      border: ${({ theme, success, disabled }) =>
        disabled ? 'none' : `1px solid ${success ? theme.colors.green : theme.colors.gray7}`};
      border-radius: 8px;
      height: ${({ multiline, customHeight }) => (multiline ? 'auto' : customHeight || '48px')};
      .MuiFilledInput-input {
        padding: ${(props) => (props.label ? '8px 12px' : '8px 12px')};
      }
      &.Mui-error {
        border: 1px solid ${({ theme }) => theme.colors.red};
      }
      &.Mui-focused {
        border: 1px solid ${({ theme }) => theme.colors.purple};

        &.Mui-error {
          border: 1px solid ${({ theme }) => theme.colors.red};
        }
      }
      &.Mui-disabled {
        background: ${({ theme }) => theme.colors.gray10};
        .MuiInputBase-input {
          color: ${({ theme }) => theme.colors.white5};
          -webkit-text-fill-color: ${({ theme }) => theme.colors.white5};
        }
      }
      &.MuiFilledInput-underline:before {
        border-bottom: unset;
      }
      &.MuiFilledInput-underline:after {
        border-bottom: unset;
      }
    }
    .MuiInputLabel-filled {
      transform: translate(10px, 15px);
      color: ${({ theme, disabled }) => (disabled ? theme.colors.gray7 : theme.colors.gray8)};
      transition: all 0.15s;
      &.MuiInputLabel-shrink {
        font-size: 10px;
        line-height: 138.5%;
        transform: translate(5px, 2px);
        color: ${({ theme }) => theme.colors.gray6};
      }
    }
    .MuiFilledInput-underline {
      padding: ${({ padding }) => padding};
    }
  }
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }
`

const Input: React.FC<InputProps> = ({
  name,
  label,
  labelSideText,
  placeholderLabel,
  placeholder,
  topDescription,
  bottomDescription,
  required,
  value,
  onChange,
  error,
  errorMsg,
  textarea,
  maxLength,
  disabled,
  success,
  successMsg,
  startIcon,
  endIcon,
  padding,
  inboxTitle,
  readOnly,
  customHeight,
  warning,
  warningMsg,
  testId,
  ...props
}) => {
  const [textLength, setTextLength] = useState(value && typeof value === 'string' ? value.length : 0)
  const theme = useTheme()

  useEffect(() => {
    setTextLength(value && typeof value === 'string' ? value.length : 0)
  }, [value])

  const getEndAdornment = () => {
    switch (true) {
      case !_.isNil(inboxTitle):
        return (
          <Text variant={TextVariant.BODY_SMALL_BOLD} color={theme.colors.gray6}>
            {inboxTitle}
          </Text>
        )
      case React.isValidElement(endIcon):
        return endIcon

      default:
        return <></>
    }
  }

  const getInputValue = () => {
    switch (true) {
      case isString(value) && !isNil(value):
        return value !== '' && value !== '0' ? value : undefined
      case isNumber(value):
        return !isNil(value) && (value as number) > 0 ? value : undefined
      default:
        return undefined
    }
  }

  return (
    <InputWrapper
      label={label}
      labelSideText={labelSideText}
      topDescription={topDescription}
      required={required}
      error={error}
      errorMsg={errorMsg}
      bottomDescription={bottomDescription}
      success={success}
      successMsg={successMsg}
      readOnly={readOnly}
      warning={warning}
      warningMsg={warningMsg}
    >
      <TextAreaWrapper>
        <StyledTextField
          data-testid={testId}
          name={name}
          success={success}
          value={getInputValue()}
          label={placeholderLabel}
          placeholder={placeholder}
          onChange={onChange}
          disabled={disabled}
          customHeight={customHeight}
          onFocus={(e) =>
            e.target.addEventListener(
              'wheel',
              (e) => {
                e.preventDefault()
              },
              { passive: false }
            )
          }
          variant="filled"
          multiline={textarea}
          padding={padding}
          error={error}
          InputProps={{
            readOnly: readOnly && true,
            startAdornment: startIcon && (
              <InputAdornment position="start" sx={{ paddingBottom: 2, marginRight: 0, marginLeft: 0 }}>
                {startIcon}
              </InputAdornment>
            ),
            endAdornment: (endIcon || !_.isNil(inboxTitle)) && (
              <InputAdornment position="start" sx={{ marginRight: 0, paddingBottom: 2 }}>
                {getEndAdornment()}
              </InputAdornment>
            )
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{ maxLength: maxLength || 255 }}
          {...props}
        />
        {textarea && (
          <LengthText variant={TextVariant.BODY_DEFAULT_BOLD}>
            {textLength} / {maxLength || 255}
          </LengthText>
        )}
      </TextAreaWrapper>
    </InputWrapper>
  )
}

export default Input
